import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FetchError, IssueDto, queryKeys, useAccessToken } from "../../..";
import { configuration } from "../../../../configuration";
import { useSignalR } from "../../../../hooks";
import { optimisticallyUpdateQueryData } from "../../utilities";

type Variables = {
  proposalGuid: string;
  issueGuid: string;
  userId: string;
};

const useRemoveProposalUpvoteRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editProposalStatusRequest(variables: Variables) {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/proposals/${variables.proposalGuid}/upvotes`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "DELETE",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editProposalStatusRequest };
};

const useRemoveProposalUpvote = () => {
  const { editProposalStatusRequest } = useRemoveProposalUpvoteRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editProposalStatusRequest,
    onMutate: async (variables) => {
      let cachedIssueData: IssueDto | undefined;

      cachedIssueData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          ...cachedData,
          proposals: cachedData.proposals.map((proposal) => {
            if (proposal.guid === variables.proposalGuid) {
              return {
                ...proposal,
                upvotes: proposal.upvotes.filter((x) => x.userId !== variables.userId),
              };
            }

            return proposal;
          }),
        })
      );

      return {
        cachedIssueData,
      };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchIssue(variables.issueGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueData);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
    },
  });
};

export { useRemoveProposalUpvote };
